import { Injectable, NgZone, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  catchError,
  firstValueFrom,
  interval,
  of,
  shareReplay,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { environment } from '../../../../environments/environment';
import { SKIP_AUTH_REFRESH } from '../../interceptors/auth-refresh-context';
import { SessionRefreshService } from './session-refresh.service';

export type MeResponse = {
  authenticated: boolean;
  userId?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  phone?: string | null;
  phoneNumber?: string | null;
  profileImageUrl?: string | null;
  profilePictureUrl?: string | null;
  avatarUrl?: string | null;
  photoUrl?: string | null;
  mfaEnabled?: boolean | null;
  accountType?: 'LANDLORD' | 'PROPERTY_MANAGER' | 'TENANT' | string | null;
  roles: string[];
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly sessionRefresh = inject(SessionRefreshService);
  private readonly zone = inject(NgZone);
  private readonly base = environment.apiGatewayUrl;
  private readonly accessTokenRefreshIntervalMs = 12 * 60 * 1000;
  private readonly cookieSettleDelayMs = 250;
  private readonly cachedSessionMaxAgeMs = 5 * 60 * 1000;
  private readonly transientVerifyGraceMs = 15 * 60 * 1000;

  private readonly sessionSubject = new BehaviorSubject<MeResponse | null>(null);
  readonly session$ = this.sessionSubject.asObservable();

  private verify$: Observable<MeResponse | null> | null = null;
  private refreshSub?: Subscription;
  private lastSessionVerifiedAt = 0;

  refreshMe() {
    this.verify$ = null;
    return this.verifySession();
  }

  refreshAccessToken(): Observable<unknown> {
    return this.refreshSession();
  }

  verifySession(): Observable<MeResponse | null> {
    if (this.verify$) {
      return this.verify$;
    }

    const url = `${this.base}/api/v1/user/auth-verify`;

    const authContext = new HttpContext().set(SKIP_AUTH_REFRESH, true);
    const verifyRequest = () => this.http.get<MeResponse>(url, {
      context: authContext,
      withCredentials: true,
    });

    this.verify$ = verifyRequest().pipe(
      tap((session) => this.acceptVerifiedSession(session)),
      catchError((error: unknown) => {
        if (!isAuthExpired(error)) {
          return this.handleVerifyFailure(error);
        }

        return this.refreshSession().pipe(
          switchMap(() => timer(this.cookieSettleDelayMs)),
          switchMap(() => verifyRequest()),
          tap((session) => this.acceptVerifiedSession(session)),
          catchError((retryError: unknown) => this.handleVerifyFailure(retryError)),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.verify$;
  }

  async ensureSession(force = false): Promise<MeResponse | null> {
    const cached = this.getSession();

    if (!force && cached?.authenticated && this.isCachedSessionFresh()) {
      return cached;
    }

    if (!force && cached?.authenticated) {
      this.verify$ = null;
    }

    return firstValueFrom(force ? this.refreshMe() : this.verifySession());
  }

  getSession(): MeResponse | null {
    return this.sessionSubject.value;
  }

  isAuthenticated(): boolean {
    return !!this.sessionSubject.value?.authenticated;
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post(`${this.base}/api/v1/user/logout`, {}, {
          responseType: 'text',
          withCredentials: true,
        }),
      );
    } finally {
      this.clearAuthState();
    }
  }

  hasAnyRole(allowedRoles: string[]): boolean {
    const roles = this.sessionSubject.value?.roles ?? [];
    return allowedRoles.some((role) => roles.includes(role));
  }

  clearAuthState(): void {
    this.verify$ = null;
    this.lastSessionVerifiedAt = 0;
    this.stopAccessRefreshHeartbeat();
    this.sessionSubject.next(null);
  }

  private acceptVerifiedSession(session: MeResponse | null): void {
    this.sessionSubject.next(session?.authenticated ? session : null);

    if (session?.authenticated) {
      this.lastSessionVerifiedAt = Date.now();
      this.startAccessRefreshHeartbeat();
    } else {
      this.lastSessionVerifiedAt = 0;
      this.stopAccessRefreshHeartbeat();
    }
  }

  private handleVerifyFailure(error?: unknown): Observable<MeResponse | null> {
    this.verify$ = null;

    const current = this.sessionSubject.value;
    if (current?.authenticated && this.shouldPreserveSessionOnVerifyError(error)) {
      return of(current);
    }

    this.lastSessionVerifiedAt = 0;
    this.stopAccessRefreshHeartbeat();
    this.sessionSubject.next(null);
    return of(null);
  }

  private isCachedSessionFresh(): boolean {
    return this.lastSessionVerifiedAt > 0
      && Date.now() - this.lastSessionVerifiedAt < this.cachedSessionMaxAgeMs;
  }

  private shouldPreserveSessionOnVerifyError(error?: unknown): boolean {
    if (!this.isWithinTransientVerifyGrace()) {
      return false;
    }

    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    return error.status === 0 || error.status === 408 || error.status === 429 || error.status >= 500;
  }

  private isWithinTransientVerifyGrace(): boolean {
    return this.lastSessionVerifiedAt > 0
      && Date.now() - this.lastSessionVerifiedAt < this.transientVerifyGraceMs;
  }

  private startAccessRefreshHeartbeat(): void {
    if (this.refreshSub || typeof window === 'undefined') {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.refreshSub = interval(this.accessTokenRefreshIntervalMs).subscribe(() => {
        this.refreshSession().subscribe({
          next: () => undefined,
          error: () => undefined,
        });
      });
    });
  }

  private stopAccessRefreshHeartbeat(): void {
    this.refreshSub?.unsubscribe();
    this.refreshSub = undefined;
  }

  private refreshSession(): Observable<unknown> {
    return this.sessionRefresh.refresh().pipe(
      tap(() => {
        this.verify$ = null;
      }),
    );
  }
}

function isAuthExpired(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }

  const errorCode = String(error.error?.error ?? error.error?.errorCode ?? '').toUpperCase();
  const message = String(error.error?.message ?? '').toLowerCase();
  const url = String(error.url ?? '').toLowerCase();
  const authEndpoint = url.includes('/api/v1/user/');

  return (
    error.status === 401 ||
    errorCode === 'AUTHORIZATION_FAILED' ||
    (error.status === 403 && authEndpoint && message.includes('authorization failed'))
  );
}
